import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Stack,
} from '@mui/material'
import { Refresh, CheckCircle, Error as ErrorIcon } from '@mui/icons-material'
import {
  getConnection,
  getCurrentUserSyncSetting,
  getAuthenticationUrl,
  testAuthentication,
  toggleSync,
  syncNow,
} from '../services/salesforce'
import type { SyncSettingWrapper } from '../types/salesforce'

export default function SettingsPage() {
  const navigate = useNavigate()
  const [syncSetting, setSyncSetting] = useState<SyncSettingWrapper | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // 認証済みかどうか
  const isAuthenticated = syncSetting?.authenticationStatus === 'Authenticated'

  // 同期が有効かどうか
  const syncEnabled = syncSetting?.syncEnabled ?? false

  // 認証ステータスに応じたバッジを表示
  const renderAuthStatusBadge = () => {
    if (!syncSetting) return null

    const statusMap = {
      Authenticated: { label: '認証済み', color: 'success' as const, icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      'Not Authenticated': { label: '未認証', color: 'default' as const, icon: <ErrorIcon sx={{ fontSize: 16 }} /> },
      'Authentication Failed': {
        label: '認証失敗',
        color: 'error' as const,
        icon: <ErrorIcon sx={{ fontSize: 16 }} />,
      },
      'Named Credential Not Configured': {
        label: '設定未完了',
        color: 'warning' as const,
        icon: <ErrorIcon sx={{ fontSize: 16 }} />,
      },
    }

    const status = statusMap[syncSetting.authenticationStatus]

    return (
      <Chip
        label={status.label}
        color={status.color}
        size="small"
        icon={status.icon}
        sx={{ fontWeight: 'bold', mb: 2 }}
      />
    )
  }

  // 初回データ取得
  useEffect(() => {
    const loadSyncSetting = async () => {
      const conn = getConnection()
      if (!conn) {
        navigate('/login')
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        const setting = await getCurrentUserSyncSetting(conn)
        setSyncSetting(setting)
      } catch (_err) {
        setSyncSetting(null)
      } finally {
        setIsLoading(false)
      }
    }

    loadSyncSetting()
  }, [navigate])

  // Google認証
  const handleAuthenticate = async () => {
    const conn = getConnection()
    if (!conn) {
      navigate('/login')
      return
    }

    try {
      setError(null)
      setSuccessMessage(null)
      const authUrl = await getAuthenticationUrl(conn)
      // Setup画面を新しいウィンドウで開く
      const authWindow = window.open(authUrl, '_blank', 'width=800,height=600')

      if (!authWindow) {
        setError('ポップアップがブロックされました。ブラウザのポップアップ設定を確認してください。')
        return
      }

      setSuccessMessage(
        '個人設定画面を新しいウィンドウで開きました。\n\n' +
          '【認証手順】\n' +
          '1. 「外部システムの認証設定」セクションを探す\n' +
          '2. 「DTP Google Calendar API」を見つける\n' +
          '3. 「認証」ボタンをクリック\n' +
          '4. Googleアカウントでログインして権限を承認\n' +
          '5. 認証完了後、このページに戻って「認証をテスト」ボタンをクリック',
      )
    } catch (err) {
      console.error('Failed to get auth URL:', err)
      setError(`認証URLの取得に失敗しました: ${err}`)
    }
  }

  // 認証テスト
  const handleTestAuthentication = async () => {
    const conn = getConnection()
    if (!conn) {
      navigate('/login')
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      setSuccessMessage(null)
      const result = await testAuthentication(conn)

      if (result.success) {
        setSuccessMessage(result.message || '認証に成功しました')
        // 設定を再取得
        const setting = await getCurrentUserSyncSetting(conn)
        setSyncSetting(setting)
      } else {
        setError(result.message || '認証に失敗しました')
        setSyncSetting(null)
      }
    } catch (err) {
      console.error('Test authentication failed:', err)
      setError('認証テストに失敗しました')
      setSyncSetting(null)
    } finally {
      setIsLoading(false)
    }
  }

  // 同期トグル
  const handleToggleSync = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked
    const conn = getConnection()
    if (!conn) {
      navigate('/login')
      return
    }

    try {
      setError(null)
      setSuccessMessage(null)
      const result = await toggleSync(conn, enabled)

      if (result.success) {
        setSuccessMessage(result.message || `同期を${enabled ? '有効' : '無効'}にしました`)
        // 設定を再取得
        const setting = await getCurrentUserSyncSetting(conn)
        setSyncSetting(setting)
      } else {
        setError(result.message || '同期設定の変更に失敗しました')
      }
    } catch (err) {
      console.error('Toggle sync failed:', err)
      setError(`同期設定の変更に失敗しました: ${err}`)
    }
  }

  // 今すぐ同期
  const handleSyncNow = async () => {
    const conn = getConnection()
    if (!conn) {
      navigate('/login')
      return
    }

    try {
      setIsSyncing(true)
      setError(null)
      setSuccessMessage(null)
      const result = await syncNow(conn)

      if (result.success) {
        setSuccessMessage(result.message || '同期が完了しました')
        // 設定を再取得
        const setting = await getCurrentUserSyncSetting(conn)
        setSyncSetting(setting)
      } else {
        setError(result.message || '同期に失敗しました')
      }
    } catch (err) {
      console.error('Sync now failed:', err)
      setError(`同期に失敗しました: ${err}`)
    } finally {
      setIsSyncing(false)
    }
  }

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Google Calendar 同期設定
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            Salesforceの面談予定をGoogle Calendarと同期する設定を行います。
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* エラー・成功メッセージ */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3, whiteSpace: 'pre-line' }} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {/* 認証ステータス */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              認証ステータス
            </Typography>
            {!isAuthenticated && (
              <Typography variant="body2" color="text.secondary" paragraph>
                Google認証が完了していません。以下の「Google 認証」ボタンをクリックして認証を行ってください。
              </Typography>
            )}
            {renderAuthStatusBadge()}
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleAuthenticate} disabled={isLoading}>
                Google 認証
              </Button>
              <Button variant="outlined" onClick={handleTestAuthentication} disabled={isLoading}>
                認証をテスト
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 同期設定 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              同期設定
            </Typography>
            <FormControlLabel
              control={<Switch checked={syncEnabled} onChange={handleToggleSync} disabled={!isAuthenticated} />}
              label={syncEnabled ? '同期有効' : '同期無効'}
            />
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              {isAuthenticated
                ? '同期を有効にすると、Salesforceの面談予定が自動的にGoogle Calendarに同期されます。'
                : '同期を有効にするには、まずGoogle認証を完了してください。'}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 手動同期 */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              手動同期
            </Typography>
            <Button
              variant="contained"
              startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
              onClick={handleSyncNow}
              disabled={!isAuthenticated || !syncEnabled || isSyncing}
            >
              {isSyncing ? '同期中...' : '今すぐ同期'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 戻るボタン */}
          <Box sx={{ textAlign: 'center' }}>
            <Button variant="text" onClick={() => navigate(-1)}>
              戻る
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}
